import React from "react";
import { connect } from "react-redux";
import { Translate, I18n } from "react-redux-i18n";
import {
  FaCheckCircle,
  FaClock,
  FaIdCard,
  FaTimesCircle,
} from "react-icons/fa";
import alertify from "alertifyjs";
import Modal from "./Modal";
import MiniLoader from "./MiniLoader";
import UserVerificationFaceRecorder from "./UserVerificationFaceRecorder";
import {
  VERIFICATION_ID_TYPE_NATIONAL,
  VERIFICATION_ID_TYPE_PASSPORT,
  VERIFICATION_STATUS_APPROVED,
  VERIFICATION_STATUS_NONE,
  VERIFICATION_STATUS_PENDING,
  VERIFICATION_STATUS_REJECTED,
} from "../assets/userVerificationUtils";
import * as userVerificationActions from "../redux/actions/userVerificationActions";
import { syncSessionVerified } from "../redux/actions/sessionActions";

const ACCEPTED_DOCUMENTS = "image/jpeg,image/png,image/webp,application/pdf";

function FileUploadField({
  id,
  labelKey,
  hintKey,
  accept,
  fileName,
  disabled,
  onChange,
}) {
  return (
    <div className="profile-verification-field">
      <label className="profile-verification-field__label" htmlFor={id}>
        <Translate value={labelKey} />
      </label>
      {hintKey && (
        <p className="profile-verification-field__hint">
          <Translate value={hintKey} />
        </p>
      )}
      <div className="profile-verification-upload">
        <input
          id={id}
          type="file"
          accept={accept}
          disabled={disabled}
          onChange={onChange}
        />
        <label htmlFor={id} className="profile-config-btn profile-verification-upload__btn">
          <Translate value="profile_configuration_verification_choose_file" />
        </label>
        <span className="profile-verification-upload__name">
          {fileName || I18n.t("profile_configuration_verification_no_file")}
        </span>
      </div>
    </div>
  );
}

class UserVerificationPanel extends React.Component {
  state = {
    modalOpen: false,
    idType: VERIFICATION_ID_TYPE_NATIONAL,
    idDocument: null,
    idDocumentName: "",
    faceVideo: null,
    addressDocument: null,
    addressDocumentName: "",
  };

  componentDidMount() {
    this.props.LoadUserVerification(this.props.user_id, this.props.token);
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.verification.id_type !== this.props.verification.id_type &&
      this.props.verification.id_type
    ) {
      this.setState({ idType: this.props.verification.id_type });
    }

    if (
      this.props.verification.status === VERIFICATION_STATUS_APPROVED &&
      prevProps.verification.status !== VERIFICATION_STATUS_APPROVED
    ) {
      this.props.SyncSessionVerified(true);
    }

    if (
      !this.props.loading &&
      prevProps.loading &&
      this.props.verification.status === VERIFICATION_STATUS_APPROVED
    ) {
      this.props.SyncSessionVerified(true);
    }
  }

  openModal = () => {
    this.setState({ modalOpen: true });
  };

  closeModal = () => {
    this.setState({ modalOpen: false });
  };

  isFormLocked = () => {
    const status = this.props.verification.status;
    return (
      status === VERIFICATION_STATUS_PENDING ||
      status === VERIFICATION_STATUS_APPROVED ||
      this.props.submitting
    );
  };

  canShowForm = () => {
    const { status } = this.props.verification;
    return (
      status === VERIFICATION_STATUS_NONE ||
      status === VERIFICATION_STATUS_REJECTED
    );
  };

  canSubmit = () => {
    if (!this.canShowForm() || this.isFormLocked()) {
      return false;
    }

    return Boolean(
      this.state.idDocument &&
        this.state.faceVideo &&
        this.state.addressDocument
    );
  };

  handleIdTypeChange = (idType) => {
    this.setState({ idType });
  };

  handleIdDocumentChange = (event) => {
    const file = event.target.files[0];
    this.setState({
      idDocument: file || null,
      idDocumentName: file ? file.name : "",
    });
  };

  handleAddressDocumentChange = (event) => {
    const file = event.target.files[0];
    this.setState({
      addressDocument: file || null,
      addressDocumentName: file ? file.name : "",
    });
  };

  handleFaceVideoChange = (file) => {
    this.setState({ faceVideo: file });
  };

  resetForm = () => {
    this.setState({
      idDocument: null,
      idDocumentName: "",
      faceVideo: null,
      addressDocument: null,
      addressDocumentName: "",
    });
  };

  handleSubmit = async () => {
    if (!this.state.idDocument) {
      alertify.warning(I18n.t("profile_configuration_verification_id_required"));
      return;
    }

    if (!this.state.faceVideo) {
      alertify.warning(I18n.t("profile_configuration_verification_face_required"));
      return;
    }

    if (!this.state.addressDocument) {
      alertify.warning(I18n.t("profile_configuration_verification_address_required"));
      return;
    }

    const result = await this.props.SubmitUserVerification({
      user_id: this.props.user_id,
      token: this.props.token,
      id_type: this.state.idType,
      id_document: this.state.idDocument,
      face_video: this.state.faceVideo,
      address_document: this.state.addressDocument,
    });

    if (!result?.success) {
      alertify.warning(I18n.t(result?.error || this.props.submitError || "try_later"));
      return;
    }

    alertify.success(I18n.t("profile_configuration_verification_sent_success"));
    this.resetForm();
  };

  getActionLabelKey = () => {
    const { status } = this.props.verification;

    if (status === VERIFICATION_STATUS_PENDING) {
      return "profile_configuration_verification_view_status";
    }

    if (status === VERIFICATION_STATUS_APPROVED) {
      return "profile_configuration_verification_view_verified";
    }

    if (status === VERIFICATION_STATUS_REJECTED) {
      return "profile_configuration_verification_resubmit";
    }

    return "profile_configuration_verification_start";
  };

  renderStatusBadge = () => {
    const { status } = this.props.verification;

    if (status === VERIFICATION_STATUS_APPROVED) {
      return (
        <span className="profile-verification-badge profile-verification-badge--approved">
          <Translate value="profile_configuration_verification_status_approved" />
        </span>
      );
    }

    if (status === VERIFICATION_STATUS_PENDING) {
      return (
        <span className="profile-verification-badge profile-verification-badge--pending">
          <Translate value="profile_configuration_verification_status_pending" />
        </span>
      );
    }

    if (status === VERIFICATION_STATUS_REJECTED) {
      return (
        <span className="profile-verification-badge profile-verification-badge--rejected">
          <Translate value="profile_configuration_verification_status_rejected" />
        </span>
      );
    }

    return (
      <span className="profile-verification-badge profile-verification-badge--none">
        <Translate value="profile_configuration_verification_status_none" />
      </span>
    );
  };

  renderSummaryText = () => {
    const { status, rejection_reason: rejectionReason } = this.props.verification;

    if (status === VERIFICATION_STATUS_APPROVED) {
      return (
        <Translate value="profile_configuration_verification_approved_hint" />
      );
    }

    if (status === VERIFICATION_STATUS_PENDING) {
      return (
        <Translate value="profile_configuration_verification_pending_hint" />
      );
    }

    if (status === VERIFICATION_STATUS_REJECTED) {
      return rejectionReason || (
        <Translate value="profile_configuration_verification_rejected_hint" />
      );
    }

    return <Translate value="profile_configuration_verification_hint" />;
  };

  renderStatusView = () => {
    const { status, rejection_reason: rejectionReason } = this.props.verification;

    if (status === VERIFICATION_STATUS_PENDING) {
      return (
        <div className="profile-verification-modal-status">
          <div className="profile-verification-modal-status__icon profile-verification-modal-status__icon--pending">
            <FaClock />
          </div>
          <h5 className="profile-verification-modal-status__title">
            <Translate value="profile_configuration_verification_status_pending" />
          </h5>
          <p className="profile-verification-modal-status__text">
            <Translate value="profile_configuration_verification_pending_hint" />
          </p>
        </div>
      );
    }

    if (status === VERIFICATION_STATUS_APPROVED) {
      return (
        <div className="profile-verification-modal-status">
          <div className="profile-verification-modal-status__icon profile-verification-modal-status__icon--approved">
            <FaCheckCircle />
          </div>
          <h5 className="profile-verification-modal-status__title">
            <Translate value="profile_configuration_verification_status_approved" />
          </h5>
          <p className="profile-verification-modal-status__text">
            <Translate value="profile_configuration_verification_approved_hint" />
          </p>
        </div>
      );
    }

    if (status === VERIFICATION_STATUS_REJECTED) {
      return (
        <div className="profile-verification-modal-status">
          <div className="profile-verification-modal-status__icon profile-verification-modal-status__icon--rejected">
            <FaTimesCircle />
          </div>
          <h5 className="profile-verification-modal-status__title">
            <Translate value="profile_configuration_verification_status_rejected" />
          </h5>
          <p className="profile-verification-modal-status__text">
            <Translate value="profile_configuration_verification_rejected_hint" />
          </p>
          {rejectionReason && (
            <div className="profile-verification-rejection">
              <strong>
                <Translate value="profile_configuration_verification_rejection_reason" />
              </strong>
              <p>{rejectionReason}</p>
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  renderForm = () => {
    const locked = this.isFormLocked();
    const { submitting } = this.props;
    const { status } = this.props.verification;
    const showRejectedIntro = status === VERIFICATION_STATUS_REJECTED;

    return (
      <div className="profile-verification-form">
        {showRejectedIntro && (
          <p className="profile-verification-modal-resubmit-hint">
            <Translate value="profile_configuration_verification_resubmit_hint" />
          </p>
        )}

        <div className="profile-verification-field">
          <span className="profile-verification-field__label">
            <Translate value="profile_configuration_verification_id_type" />
          </span>
          <div className="profile-config-option-list profile-config-option-list--row">
            <label
              className={`profile-config-option${
                this.state.idType === VERIFICATION_ID_TYPE_NATIONAL
                  ? " profile-config-option--selected"
                  : ""
              }`}
            >
              <input
                type="radio"
                name="verificationIdType"
                value={VERIFICATION_ID_TYPE_NATIONAL}
                checked={this.state.idType === VERIFICATION_ID_TYPE_NATIONAL}
                disabled={locked}
                onChange={() =>
                  this.handleIdTypeChange(VERIFICATION_ID_TYPE_NATIONAL)
                }
              />
              <span className="profile-config-option__label">
                <Translate value="profile_configuration_verification_national_id" />
              </span>
            </label>
            <label
              className={`profile-config-option${
                this.state.idType === VERIFICATION_ID_TYPE_PASSPORT
                  ? " profile-config-option--selected"
                  : ""
              }`}
            >
              <input
                type="radio"
                name="verificationIdType"
                value={VERIFICATION_ID_TYPE_PASSPORT}
                checked={this.state.idType === VERIFICATION_ID_TYPE_PASSPORT}
                disabled={locked}
                onChange={() =>
                  this.handleIdTypeChange(VERIFICATION_ID_TYPE_PASSPORT)
                }
              />
              <span className="profile-config-option__label">
                <Translate value="profile_configuration_verification_passport" />
              </span>
            </label>
          </div>
        </div>

        <FileUploadField
          id="verification-id-document"
          labelKey="profile_configuration_verification_id_document"
          hintKey="profile_configuration_verification_id_hint"
          accept={ACCEPTED_DOCUMENTS}
          fileName={this.state.idDocumentName}
          disabled={locked}
          onChange={this.handleIdDocumentChange}
        />

        <div className="profile-verification-field">
          <span className="profile-verification-field__label">
            <Translate value="profile_configuration_verification_face_video" />
          </span>
          <p className="profile-verification-field__hint">
            <Translate value="profile_configuration_verification_face_desc" />
          </p>
          <UserVerificationFaceRecorder
            active={this.state.modalOpen && !locked}
            disabled={locked}
            onVideoChange={this.handleFaceVideoChange}
          />
          {this.state.faceVideo && (
            <p className="profile-verification-file-ready">
              <Translate value="profile_configuration_verification_video_ready" />
            </p>
          )}
        </div>

        <FileUploadField
          id="verification-address-document"
          labelKey="profile_configuration_verification_address_document"
          hintKey="profile_configuration_verification_address_hint"
          accept={ACCEPTED_DOCUMENTS}
          fileName={this.state.addressDocumentName}
          disabled={locked}
          onChange={this.handleAddressDocumentChange}
        />

        <div className="profile-config-actions">
          <button
            type="button"
            className="profile-config-btn profile-config-btn--success"
            onClick={this.handleSubmit}
            disabled={!this.canSubmit() || submitting}
          >
            <Translate value="profile_configuration_verification_submit" />
          </button>
        </div>
      </div>
    );
  };

  renderModalContent = () => {
    const { loading } = this.props;

    if (loading) {
      return (
        <div className="profile-config-loading">
          <MiniLoader />
        </div>
      );
    }

    const { status } = this.props.verification;
    const statusView = this.renderStatusView();

    if (status === VERIFICATION_STATUS_NONE) {
      return this.renderForm();
    }

    if (status === VERIFICATION_STATUS_REJECTED) {
      return (
        <>
          {statusView}
          {this.renderForm()}
        </>
      );
    }

    return statusView;
  };

  render() {
    return (
      <>
        <section className="profile-data-section profile-verification-section">
          <div className="profile-verification-section__header">
            <div className="profile-verification-section__intro">
              <div className="profile-verification-section__title-row">
                <span className="profile-verification-section__icon" aria-hidden="true">
                  <FaIdCard />
                </span>
                <h4 className="profile-data-section__title">
                  <Translate value="profile_configuration_verification" />
                </h4>
              </div>
              <p className="profile-config-section-hint">
                {this.renderSummaryText()}
              </p>
            </div>
            {this.renderStatusBadge()}
          </div>

          <button
            type="button"
            className="profile-config-btn profile-verification-open-btn"
            onClick={this.openModal}
          >
            <Translate value={this.getActionLabelKey()} />
          </button>
        </section>

        <Modal
          isOpen={this.state.modalOpen}
          onClose={this.closeModal}
          title={I18n.t("profile_configuration_verification")}
          large
        >
          <div className="profile-verification-modal">
            <div className="profile-verification-modal__badge-row">
              {this.renderStatusBadge()}
            </div>
            <div className="profile-verification-modal__body">
              {this.renderModalContent()}
            </div>
          </div>
        </Modal>
      </>
    );
  }
}

function mapStateToProps(reducer) {
  return {
    verification: reducer.userVerification.data,
    loading: reducer.userVerification.loading,
    submitting: reducer.userVerification.submitting,
    submitError: reducer.userVerification.submitError,
  };
}

const mapDispatchToProps = (dispatch) => ({
  LoadUserVerification: (user_id, token) =>
    dispatch(userVerificationActions.loadUserVerification({ user_id, token })),
  SubmitUserVerification: (data) =>
    dispatch(userVerificationActions.submitUserVerification(data)),
  SyncSessionVerified: (verified) => dispatch(syncSessionVerified(verified)),
});

export default connect(mapStateToProps, mapDispatchToProps)(UserVerificationPanel);
